/**
 * Helpers to compose URLs that the backend streams with HTTP Range
 * support. Keeping them in one file so the components don't hardcode
 * `/api/v1/media/...` paths scattered around.
 */

import { useQuery } from '@tanstack/react-query'
import { api, authFetch } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import { apiUrl } from '@/lib/apiUrl'
import type { components } from '@/../types/api'

export type SubtitleStyle = components['schemas']['SubtitleStyleOut']

/** Catalog entry returned by ``GET /api/v1/media/thumbnail-styles``. Defined
 * locally because the OpenAPI typegen hasn't been re-run for the new
 * endpoint yet — same shape as ``SubtitleStyle``. */
export interface ThumbnailStyle {
  key: string
  label: string
}

/**
 * Build a streamable URL for the generated MP4. The backend resolves the
 * relative ``output/...`` path and returns the file with ``Accept-Ranges``
 * so HTML ``<video>`` can seek without downloading everything.
 */
export function videoStreamUrl(relativePath: string | null | undefined): string | null {
  if (!relativePath) return null
  return apiUrl(`/api/v1/media/videos?path=${encodeURIComponent(relativePath)}`)
}

/**
 * Same MP4 as `videoStreamUrl`, served as an attachment saved as
 * ``filename``. The backend sets ``Content-Disposition`` itself: in
 * production the SPA and the API are different origins, and there the
 * ``download`` attribute of an ``<a>`` is ignored.
 */
export function videoDownloadUrl(relativePath: string | null | undefined, filename: string): string | null {
  if (!relativePath) return null
  const params = new URLSearchParams({ path: relativePath, download: filename })
  return apiUrl(`/api/v1/media/videos?${params.toString()}`)
}

/**
 * Thumbnail URL. Same conventions as `videoStreamUrl`. With ``width`` (240,
 * 360 or 540) the backend serves a reduced WebP instead of the 1080×1920 PNG:
 * the grid shows ~250 px and the PNG is ~1 MB. An old backend ignores the
 * parameter and serves the PNG.
 */
export function thumbnailUrl(relativePath: string | null | undefined, width?: 240 | 360 | 540): string | null {
  if (!relativePath) return null
  const params = new URLSearchParams({ path: relativePath })
  if (width) params.set('w', String(width))
  return apiUrl(`/api/v1/media/thumbnails?${params.toString()}`)
}

/**
 * Audio (MP3) URL — narration files generated by the TTS service. Backed
 * by ``GET /api/v1/media/audio`` which streams with ``Accept-Ranges`` so
 * the native ``<audio>`` element can seek.
 */
export function audioStreamUrl(relativePath: string | null | undefined): string | null {
  if (!relativePath) return null
  return apiUrl(`/api/v1/media/audio?path=${encodeURIComponent(relativePath)}`)
}

/**
 * URL of a server-rendered PNG preview for a subtitle style — same
 * Pillow renderer the pipeline uses, so previews are WYSIWYG. Cheap
 * enough to fetch on demand; the browser caches it.
 */
export function subtitlePreviewUrl(styleKey: string, color?: string): string {
  const params = new URLSearchParams({ style: styleKey })
  // Só o ``karaoke`` usa a cor (a da palavra sendo lida); os outros ignoram.
  if (color) params.set('color', color)
  return apiUrl(`/api/v1/media/subtitle-preview?${params.toString()}`)
}

/**
 * Catalog of subtitle styles the renderer supports. The backend
 * introspects ``src.services.video.subtitle_styles.STYLES`` so adding a
 * new style there propagates here without frontend changes. Long
 * staleTime — the catalog is static once the server is up.
 */
export function useSubtitleStyles() {
  return useQuery({
    queryKey: queryKeys.media.subtitleStyles(),
    queryFn: async (): Promise<SubtitleStyle[]> => {
      const { data } = await api.GET('/api/v1/media/subtitle-styles', { params: {} })
      return data?.data ?? []
    },
    staleTime: 5 * 60_000,
  })
}

/**
 * Server-rendered PNG preview for a thumbnail style — Pillow-rendered at
 * a 9:16 mini-canvas (~270×480) so the carousel cards stay cheap to load.
 * The optional ``color`` lets the carousel reflect the user's currently
 * picked accent (today only ``glow_burst`` consumes it; other styles
 * silently ignore it).
 */
export function thumbnailPreviewUrl(styleKey: string, color?: string): string {
  const params = new URLSearchParams({ style: styleKey })
  if (color) params.set('color', color)
  return apiUrl(`/api/v1/media/thumbnail-preview?${params.toString()}`)
}

/**
 * Catalog of thumbnail styles. Mirrors ``useSubtitleStyles``: introspects
 * ``_STYLE_TEMPLATES`` on the backend so adding a new HTML template under
 * ``src/web/templates/renderer/`` shows up here automatically.
 */
export function useThumbnailStyles() {
  return useQuery({
    queryKey: queryKeys.media.thumbnailStyles(),
    queryFn: async (): Promise<ThumbnailStyle[]> => {
      // Endpoint not yet in the OpenAPI typegen — go through plain fetch.
      const res = await authFetch('/api/v1/media/thumbnail-styles', {
        credentials: 'same-origin',
      })
      if (!res.ok) return []
      const body = (await res.json()) as { data?: ThumbnailStyle[] }
      return body.data ?? []
    },
    staleTime: 5 * 60_000,
  })
}

/** One voice a TTS provider can synthesise. ``provider`` lets the UI
 * group / badge by source when ``provider='all'`` returns a mixed list. */
export interface TtsVoice {
  value: string
  name: string
  gender: string
  language: string
  is_default: boolean
  provider: string
}

interface TtsVoicesResponse {
  provider: string
  voices: TtsVoice[]
}

/**
 * Voices available for narration, optionally filtered to one language.
 *
 * - ``provider=''`` (default) → uses the backend's active provider.
 * - ``provider='all'`` → aggregate every registered provider in one
 *   list. The factory forms use this so a single carousel can show
 *   Edge + Kokoro + Piper voices side by side; routing to the right
 *   engine at synthesis time happens server-side via the voice's
 *   ``provider`` field on each entry.
 */
export function useTtsVoices(language: '' | 'pt-br' | 'en' = '', provider = '') {
  return useQuery({
    queryKey: queryKeys.media.ttsVoices(language, provider),
    queryFn: async (): Promise<TtsVoicesResponse> => {
      const params = new URLSearchParams()
      if (language) params.set('language', language)
      if (provider) params.set('provider', provider)
      const url = `/api/v1/media/tts-voices${params.toString() ? `?${params.toString()}` : ''}`
      const res = await authFetch(url, { credentials: 'same-origin' })
      if (!res.ok) return { provider: '', voices: [] }
      const body = (await res.json()) as { data?: TtsVoicesResponse }
      return body.data ?? { provider: '', voices: [] }
    },
    staleTime: 5 * 60_000,
  })
}
